import * as React from 'react';
import { Component } from 'react';
import { get, isEmpty, isNaN, isArray } from 'lodash';
import { Form, Grid } from 'semantic-grab-ui/src';
import TridentCampaignQuickFilters from 'omega-rtc/components/custom/TridentCampaignQuickFilters';
import TridentCampaignAdvancedFilters from 'omega-rtc/components/custom/TridentCampaignAdvancedFilters';
import './TridentCampaignNewSearchForm.scss';
import { statusList, targetTypeList } from 'data/trident-campaigns';
import { Country } from 'types/country';

interface Filter {
  lhs: string;
  operator: string;
  rhs: any;
}

interface SearchData {
  filters: Filter[];
  searchText: string;
  country: string | number;
  status: string;
  userType: string;
  myCampaign: boolean;
  pendingApproval: boolean;
}

interface Props {
  searchParams: Record<string, any>;
  filterTypes: Record<string, any>;
  countries: Country[];
  onSearch: (searchData: SearchData) => void;
  hasApproverPermission?: boolean;
}

interface State {
  filters: Filter[];
  searchText: string;
  country: string | number;
  status: string;
  userType: string;
  myCampaign: boolean;
  pendingApproval: boolean;
}

// TridentCampaignNewSearchForm is a pure form component
// with a text search bar and additional add-on filters
//
// Props:
// - onSearch: callback function that receives search data object with:
//   {filters, searchText, country, status, userType, myCampaign}
class TridentCampaignNewSearchForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      // filters will be array of objects {lhs: string, operator: string, rhs: any}
      filters: [],
      searchText: '',
      // below are quick filters
      country: '',
      status: '', // Changed back to string for SingleSelect
      userType: '', // Changed back to string for SingleSelect
      myCampaign: false,
      pendingApproval: false,
    };
  }

  componentDidMount() {
    this.processSearchParams();
  }

  processSearchParams = () => {
    const { searchParams } = this.props;
    const encodedFilters = get(searchParams, 'filters', '');
    if (!isEmpty(encodedFilters)) {
      this.setState({
        filters: JSON.parse(encodedFilters),
      });
    }

    // Handle single values from URL
    this.setState({
      searchText: get(searchParams, 'searchText', ''),
      status: get(searchParams, 'status', ''),
      userType: get(searchParams, 'userType', ''),
    });

    if (get(searchParams, 'myCampaign', '') === 'true') {
      this.setState({
        myCampaign: true,
      });
    }

    if (get(searchParams, 'pendingApproval', '') === 'true') {
      this.setState({
        pendingApproval: true,
      });
    }

    const countryID = Number(get(searchParams, 'country', ''));
    if (!isNaN(countryID) && countryID > 0) {
      this.setState({
        country: String(countryID),
      });
    }
  };

  // Handle advanced filters change
  handleAdvancedFiltersChange = (filters: Filter[]) => {
    const newState = {
      ...this.state,
      filters,
    };
    this.setState(newState);
    this.submitSearch(newState);
  };

  // processFilters sets the rhs in filters to correct data type
  processFilters = (filters: Filter[]): Filter[] => {
    return filters.map((filter) => {
      const processed = { ...filter };
      const dataType = get(
        this.props.filterTypes,
        `${filter.lhs}.type`,
        'string',
      );
      if (dataType === 'number') {
        if (!isArray(filter.rhs)) {
          processed.rhs = Number(filter.rhs);
        } else {
          processed.rhs = filter.rhs.map((val: any) => {
            return Number(val);
          });
        }
      }
      return processed;
    });
  };

  // validateSearchText returns isValid (bool)
  validateSearchText = (text: string): boolean => {
    if (isEmpty(text)) {
      return true;
    }
    const stripped = text.split(' ').join(''); // remove space in the string
    if (stripped.length < 4) {
      alert('please type in at least 4 characters to search');
      return false;
    }
    return true;
  };

  // submitSearch processes filters and calls the onSearch callback
  submitSearch = (params: State) => {
    if (!this.validateSearchText(params.searchText)) {
      return;
    }

    const processedFilters = this.processFilters(params.filters);
    const searchData: SearchData = {
      filters: processedFilters,
      searchText: params.searchText,
      country: params.country,
      status: params.status,
      userType: params.userType,
      myCampaign: params.myCampaign,
      pendingApproval: params.pendingApproval,
    };

    // Call the callback function
    this.props.onSearch(searchData);
  };

  onInputChange = (name: string, data: { value: string }) => {
    const newState = {
      ...this.state,
      [name]: data.value,
    };
    this.setState(newState);
  };

  // Handler for QuickFilters component
  onQuickFilterChange = (key: string, value: any) => {
    const newState = {
      ...this.state,
      [key]: value || (typeof value === 'boolean' ? value : ''),
    };

    // Ensure mutual exclusivity between myCampaign and pendingApproval
    if (key === 'pendingApproval' && value === true) {
      newState.myCampaign = false;
    } else if (key === 'myCampaign' && value === true) {
      newState.pendingApproval = false;
    }

    this.setState(newState);

    this.submitSearch(newState);
  };

  // Generate field configurations for QuickFilters
  getQuickFilterConfigs = () => {
    const countryOptions = (this.props.countries || []).map((country) => ({
      label: country.name,
      value: String(country.countryID),
    }));

    const statusOptions = statusList.map((status) => ({
      label: status.text,
      value: status.value,
    }));

    const userTypeOptions = targetTypeList.map((userType) => ({
      label: userType.text,
      value: userType.value,
    }));

    const configs: Record<string, any> = {
      country: {
        name: 'Country',
        type: 'singleSelect',
        options: countryOptions,
        allowEmpty: true,
        width: '150px',
        height: '36px',
      },
      status: {
        name: 'Status',
        type: 'singleSelect',
        options: statusOptions,
        allowEmpty: true,
        width: '150px',
        height: '36px',
      },
      userType: {
        name: 'User Type',
        type: 'singleSelect',
        options: userTypeOptions,
        allowEmpty: true,
        width: '180px',
        height: '36px',
      },
      myCampaign: {
        name: 'My Campaign Only',
        type: 'toggle',
        width: '140px',
        height: '36px',
      },
    };

    // Only show "Pending My Approval" button if user has approver permission
    if (this.props.hasApproverPermission) {
      configs.pendingApproval = {
        name: 'Pending My Approval',
        type: 'toggle',
        width: '185px',
        height: '36px',
      };
    }

    return configs;
  };

  updateState = (param: Partial<State>) => {
    this.setState({
      ...this.state,
      ...param,
    });
    // Note: No auto-search - only search on explicit button click
  };

  render() {
    const { filters } = this.state;

    return (
      <Form id="newTridentSearchForm">
        <div id="modalPlaceholder" />
        <Form.Field>
          <Grid>
            <Grid.Row>
              <Grid.Column width={14}>
                <Form.Input
                  placeholder="Search for campaign name ONLY"
                  width={16}
                  onChange={(_, data) => {
                    return this.onInputChange('searchText', data);
                  }}
                  value={this.state.searchText}
                />
              </Grid.Column>
              <Grid.Column width={2}>
                <Form.Button
                  content="Go"
                  onClick={() => {
                    this.submitSearch(this.state);
                  }}
                  positive
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className="quick-filter-row">
              <Grid.Column width={12}>
                <TridentCampaignQuickFilters
                  fieldConfigs={this.getQuickFilterConfigs()}
                  fieldValues={{
                    country: this.state.country,
                    status: this.state.status,
                    userType: this.state.userType,
                    myCampaign: this.state.myCampaign,
                    pendingApproval: this.state.pendingApproval,
                  }}
                  onChange={this.onQuickFilterChange}
                />
              </Grid.Column>
            </Grid.Row>
            <TridentCampaignAdvancedFilters
              filters={filters}
              filterTypes={this.props.filterTypes}
              onChange={this.handleAdvancedFiltersChange}
            />
          </Grid>
        </Form.Field>
      </Form>
    );
  }
}

export default TridentCampaignNewSearchForm;
